import { Injectable } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { throwError, Observable, of } from 'rxjs';

export interface ErrorHandlingOptions {
  showToast?: boolean;
  customMessage?: string;
  logToConsole?: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class ErrorHandlingService {
  
  constructor() {}

  /**
   * Handle HTTP errors with comprehensive logic and user-friendly messages
   */
  handleError(error: HttpErrorResponse, options: ErrorHandlingOptions = {}): Observable<never> {
    const { showToast = true, customMessage, logToConsole = true } = options;
    
    let errorMessage = customMessage || this.getErrorMessage(error);
    
    // Log error for debugging
    if (logToConsole) {
      console.group('🚨 HTTP Error Detected');
      console.error('Error:', error);
      console.error('Status:', error.status);
      console.error('Message:', errorMessage);
      console.groupEnd();
    }
    
    // Show user-friendly toast message
    if (showToast) {
      this.showUserFriendlyError(error, errorMessage);
    }
    
    return throwError(() => error);
  }

  /**
   * Handle HTTP errors without throwing (for non-critical operations)
   */
  handleSilentError(error: HttpErrorResponse, options: ErrorHandlingOptions = {}): Observable<null> {
    const { logToConsole = false } = options;
    
    if (logToConsole) {
      console.warn('🤫 Silent Error (non-critical):', error);
    }
    
    return of(null);
  }

  /**
   * Generate user-friendly error messages based on error type
   */
  private getErrorMessage(error: HttpErrorResponse): string {
    // Network errors
    if (!navigator.onLine) {
      return 'You appear to be offline. Please check your internet connection.';
    }
    
    if (error.error instanceof ErrorEvent) {
      // Client-side/network error
      if (error.error.message.includes('NetworkError')) {
        return 'Network connection failed. Please check your internet connection and try again.';
      }
      return `Connection issue: ${error.error.message}`;
    }
    
    // Server-side errors
    switch (error.status) {
      case 0:
        return 'Unable to connect to our servers. Please check your internet connection.';
      
      case 400:
        return 'Invalid request. Please check your input and try again.';
      
      case 401:
        return 'Your session has expired. Please log in again.';
      
      case 403:
        return 'You do not have permission to perform this action.';
      
      case 404:
        return 'The requested resource was not found.';
      
      case 408:
        return 'Request timed out. Please try again.';
      
      case 429:
        return 'Too many requests. Please wait a moment and try again.';
      
      case 500:
        return 'Server error occurred. Please try again later.';
      
      case 502:
        return 'Service temporarily unavailable. Please try again in a few minutes.';
      
      case 503:
        return 'Service is currently unavailable. Please try again later.';
      
      case 504:
        return 'Server took too long to respond. Please try again.';
      
      default:
        if (error.status >= 400 && error.status < 500) {
          return 'Request error. Please check your input and try again.';
        } else if (error.status >= 500) {
          return 'Server error occurred. Our team has been notified.';
        }
        return 'An unexpected error occurred. Please try again.';
    }
  }

  /**
   * Show user-friendly error message (to be implemented with actual toast service)
   */
  private showUserFriendlyError(error: HttpErrorResponse, message: string): void {
    // This will be implemented when toast service is available
    // For now, we'll use a user-friendly alert in production
    if (error.status === 401) {
      // Auth errors should be handled by interceptor, but just in case
      return;
    }
    
    // You can replace this with your actual toast service
    if (typeof window !== 'undefined' && (window as any)['Swal']) {
      (window as any)['Swal'].fire({
        icon: 'error',
        title: 'Error',
        text: message,
        confirmButtonColor: '#3f6ad8',
        timer: 5000,
        timerProgressBar: true
      });
    }
  }

  /**
   * Check if error is recoverable (should retry)
   */
  isRecoverableError(error: HttpErrorResponse): boolean {
    // Don't retry on client errors (4xx)
    if (error.status >= 400 && error.status < 500) {
      return false;
    }
    
    // Retry on server errors and network issues
    return error.status >= 500 || error.status === 0 || error.status === 408;
  }

  /**
   * Get retry delay with exponential backoff
   */
  getRetryDelay(attempt: number, baseDelay: number = 1000): number {
    const maxDelay = 30000; // 30 seconds max
    const delay = baseDelay * Math.pow(2, attempt - 1);
    const jitter = delay * 0.1 * Math.random(); // Add jitter to prevent thundering herd
    
    return Math.min(delay + jitter, maxDelay);
  }
}